import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, User as UserIcon, Globe } from "@phosphor-icons/react";
import CountryPicker from "@/components/CountryPicker";

function daysSince(iso) {
  if (!iso) return null;
  try {
    return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  } catch { return null; }
}

function cooldownInfo(updatedAt, cooldownDays = 30) {
  const d = daysSince(updatedAt);
  if (d == null) return { locked: false, daysLeft: 0 };
  const remaining = cooldownDays - d;
  return { locked: remaining > 0, daysLeft: Math.max(0, remaining) };
}

/** Dialog for editing username + country.
 *  Both fields have a 30-day cooldown enforced server-side. UI surfaces the
 *  cooldown so the user knows when they can change next.
 */
export default function EditProfileDialog({ user, onClose, onSaved }) {
  const [username, setUsername] = useState(user.username || "");
  const [country, setCountry] = useState(user.country || "");
  const [busy, setBusy] = useState(false);

  const unameCD = cooldownInfo(user.username_updated_at);
  const ctryCD = cooldownInfo(user.country_updated_at);

  const hasChanges =
    (username && username !== user.username) ||
    (country && country !== user.country);

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !hasChanges) return;
    const payload = {};
    if (username && username !== user.username) {
      if (unameCD.locked) {
        toast.error(`Username locked for ${unameCD.daysLeft} more day(s).`);
        return;
      }
      payload.username = username.trim();
    }
    if (country && country !== user.country) {
      if (ctryCD.locked) {
        toast.error(`Country locked for ${ctryCD.daysLeft} more day(s).`);
        return;
      }
      payload.country = country;
    }
    setBusy(true);
    try {
      const { data } = await api.patch("/auth/me", payload);
      toast.success("Profile updated");
      onSaved(data.user || payload);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save changes");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
         data-testid="edit-profile-dialog">
      <form onSubmit={submit} className="w-full max-w-md bg-nk-bg border border-nk-line rounded-sm">
        <div className="p-5 border-b border-nk-line/40 flex items-center justify-between">
          <p className="font-display text-lg font-medium">Edit profile</p>
          <button type="button" onClick={onClose} aria-label="Close"
                  data-testid="edit-profile-close"
                  className="p-1.5 text-nk-mute hover:text-nk-text">
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Username */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-nk-mute flex items-center gap-1.5 mb-1.5">
              <UserIcon size={11} /> Username
            </label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={unameCD.locked}
              placeholder="3-20 chars · letters, numbers, _"
              maxLength={20}
              data-testid="edit-username-input"
              className="w-full bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-accent outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <p className="text-[10px] text-nk-dim mt-1">
              {unameCD.locked
                ? `Locked — can change in ${unameCD.daysLeft} day${unameCD.daysLeft === 1 ? "" : "s"}.`
                : user.username_updated_at
                  ? "Free to change. Next change will lock for 30 days."
                  : "First change is free. After that, locked for 30 days."}
            </p>
          </div>

          {/* Country */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-nk-mute flex items-center gap-1.5 mb-1.5">
              <Globe size={11} /> Country
            </label>
            {ctryCD.locked ? (
              <p className="px-3 py-3 text-sm text-nk-mute border border-nk-line rounded-sm">
                Locked — can change in {ctryCD.daysLeft} day{ctryCD.daysLeft === 1 ? "" : "s"}.
              </p>
            ) : (
              <CountryPicker value={country} onChange={setCountry} testidPrefix="edit-country" />
            )}
            <p className="text-[10px] text-nk-dim mt-1">
              You compete for this country's leaderboard. 30-day cooldown after change.
            </p>
          </div>
        </div>

        <div className="p-5 border-t border-nk-line/40 flex gap-2">
          <button type="button" onClick={onClose}
                  className="px-4 py-2 text-sm border border-nk-line text-nk-mute hover:text-nk-text rounded-sm">
            Cancel
          </button>
          <button type="submit" disabled={busy || !hasChanges}
                  data-testid="edit-profile-save"
                  className="flex-1 px-4 py-2 bg-nk-accent text-nk-bg text-sm font-medium rounded-sm hover:bg-nk-accent/90 disabled:opacity-40">
            {busy ? "Saving…" : hasChanges ? "Save changes" : "No changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
